/**
 * Hook standardisé pour les requêtes GET avec React Query
 * Remplace le custom useApiQuery qui violait les règles CONETEXT.md
 * 
 * ✅ Conforme aux règles : Pas de useEffect pour data fetching
 * ✅ Utilise React Query natif pour cache, refetch, invalidation
 */

import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { authenticatedApi, publicApi } from '../Api/api';
import { AxiosError } from 'axios';

interface StandardQueryOptions<TData> {
  /** Paramètres de la requête GET */
  params?: Record<string, any>;
  
  /** Active/désactive la requête */
  enabled?: boolean;
  
  /** Temps avant que les données soient considérées comme obsolètes (ms) */
  staleTime?: number;
  
  /** Utiliser l'API publique au lieu de l'API authentifiée */
  usePublicApi?: boolean;
  
  /** Options React Query supplémentaires */
  queryOptions?: Omit<UseQueryOptions<TData, AxiosError>, 'queryKey' | 'queryFn'>;
}

/**
 * Hook pour effectuer une requête GET avec React Query
 * 
 * @example
 * ```typescript
 * const { data, isLoading, error, refetch } = useStandardQuery<Society[]>(
 *   ['societies', filters],
 *   '/api/societies',
 *   { params: filters }
 * );
 * ```
 */
export function useStandardQuery<TData = any>(
  queryKey: unknown[],
  relativeUrl: string,
  options: StandardQueryOptions<TData> = {}
): UseQueryResult<TData, AxiosError> {
  const { params, enabled = true, staleTime, usePublicApi = false, queryOptions } = options;
  
  const api = usePublicApi ? publicApi : authenticatedApi;

  return useQuery<TData, AxiosError>({
    queryKey: [...queryKey, params], // Inclure params dans la clé pour auto-refetch
    queryFn: async () => {
      const response = await api.get(relativeUrl, { params });
      return response.data;
    },
    enabled,
    staleTime,
    ...queryOptions,
  });
}

/**
 * Hook pour les requêtes de listes paginées
 * 
 * @example
 * ```typescript
 * const { data, isLoading, fetchNextPage, hasNextPage } = useStandardListQuery<Society>(
 *   ['societies'],
 *   '/api/societies',
 *   { params: { search: 'test' }, pageSize: 20 }
 * );
 * ```
 */
export function useStandardListQuery<TData = any>(
  queryKey: unknown[],
  relativeUrl: string,
  options: StandardQueryOptions<TData> & { pageSize?: number } = {}
) {
  const { params, pageSize = 10, ...restOptions } = options;

  return useStandardQuery<{
    nodes: TData[];
    pageInfos: {
      currentPage: number;
      nextPage: number | null;
      hasNextPage: boolean;
      totalPages: number;
      totalCount: number;
      hasPreviousPage: boolean;
      previousPage: number | null;
    };
  }>(
    queryKey,
    relativeUrl,
    {
      ...restOptions,
      params: { ...params, total: pageSize },
    } as any
  );
}

